import { db } from './api';
import { collection, doc, getDocs, query, where, 
    runTransaction, Timestamp, setDoc, deleteDoc, documentId} from "firebase/firestore";



/**
 * Función que crea una mascota
 * @param {string} userId id del usuario
 * @param {string} name nombre de la mascota
 * @param {string} species especie de la mascota
 * @param {string} breed raza de la mascota
 * @param {string} birth fecha de nacimiento de la mascota
 * @param {string} weight peso de la mascota
 * @param {string} color color de la mascota
 * @returns {boolean} retorna verdadero si creo la mascota
 */
export const createPet = async (userId, name, species, breed, birth, weight, color, sex) => {
    try {
        const petsRef = collection(db, "pets");
        // Verificamos si el usuario ya tiene una mascota
        const q = query(petsRef, where("creatorId", "==", userId));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.size > 0) {
            // Si el usuario ya tiene una mascota, no permitimos más
            return false;
        }

        let petId;
        await runTransaction(db, async (transaction) => {
            const newPetRef = doc(petsRef);
            petId = newPetRef.id; // Guardamos el ID de la nueva mascota para usarlo luego

            // Creamos el nuevo documento en la colección "pets"
            transaction.set(newPetRef, {
                creatorId: userId,
                name: name,
                species: species,
                breed: breed,
                birth: Timestamp.fromDate(new Date(birth)), // Usamos Timestamp para la fecha
                weight: weight,
                color: color,
                sex: sex,
                created: Timestamp.fromDate(new Date()), // Fecha actual
                updated: Timestamp.fromDate(new Date()), // Fecha actual
            });

            // Ahora, creamos un registro en "petsOwnerUser"
            const petsOwnerUserRef = doc(collection(db, "petsOwnerUser"));
            transaction.set(petsOwnerUserRef, {
                userId: userId,
                petId: petId,
                createdAt: Timestamp.fromDate(new Date()), // Fecha de la operación
            });
        });

        return true; // La transacción fue exitosa
    } catch (error) {
        console.error("Transaction failed: ", error);
        return false; // La transacción falló
    }
}

/**
 * Función que borra una mascota y sus relaciones (vacunas, historial médico y relación con el usuario)
 * @param {string} petId id de la mascota
 * @returns {boolean} retorna verdadero si borro la mascota
 */
export const deletePet = async (petId) => {
    try {
        await runTransaction(db, async (transaction) => {
            const petRef = doc(db, "pets", petId);
            transaction.delete(petRef);

            const petsOwnerUserRef = collection(db, "petsOwnerUser");
            const q = query(petsOwnerUserRef, where("petId", "==", petId));
            const querySnapshot = await getDocs(q);
            querySnapshot.forEach((doc) => {
                transaction.delete(doc.ref);
            });

            const vaccinesRef = collection(db, "vaccines");
            const qVaccines = query(vaccinesRef, where("petId", "==", petId));
            const querySnapshotVaccines = await getDocs(qVaccines);
            querySnapshotVaccines.forEach((doc) => {
                transaction.delete(doc.ref);
            });

            const medicalHistoryRef = collection(db, "medicalHistory");
            const qMedicalHistory = query(medicalHistoryRef, where("petId", "==", petId));
            const querySnapshotMedicalHistory = await getDocs(qMedicalHistory);
            querySnapshotMedicalHistory.forEach((doc) => {
                transaction.delete(doc.ref);
            });
        });
    } catch (error) {
        console.error(error);
        return false;
    }
}

/**
 * Función que obtiene las mascotas de un usuario
 * @param {string} userId id del usuario
 * @returns {array} retorna un arreglo con las mascotas del usuario
 */
export const getPetsByOwner = async (userId) => {
    try {
        // Obtenemos las relaciones usuario-mascota de "petsOwnerUser"
        const petsOwnerUserRef = collection(db, "petsOwnerUser");
        const relationsQuery = query(petsOwnerUserRef, where("userId", "==", userId));
        const relationsSnapshot = await getDocs(relationsQuery);

        let petIds = [];
        relationsSnapshot.forEach((doc) => {
            petIds.push(doc.data().petId); // Recolectamos los IDs de las mascotas
        });

        // Si no hay mascotas, retornamos un arreglo vacío
        if (petIds.length === 0) {
            return [];
        }

        // Realizamos una consulta en la colección "pets" con todos los IDs de las mascotas
        const petsRef = collection(db, "pets");
        const petsQuery = query(petsRef, where(documentId(), "in", petIds));
        const petsSnapshot = await getDocs(petsQuery);

        let petsArray = [];
        petsSnapshot.forEach((doc) => {
            let pet = doc.data();
            pet.id = doc.id; // Agregamos manualmente el ID al objeto de la mascota
            petsArray.push(pet);
        });

        return petsArray;
    } catch (error) {
        console.error("Error getting pets by owner:", error);
        return [];
    }
}


/**
 * Agrega una vacuna a una mascota
 * @param {string} petId id de la mascota
 * @param {string} date fecha de la vacuna
 * @param {string} name nombre de la vacuna
 * @param {string} userId id del usuario que agrega la vacuna
 * @returns 
 */
export const addVaccineToPet = async (petId, date, name, userId) => {
    try {
        const vaccine = collection(db, "vaccines");
        const newVaccine = await setDoc(doc(vaccine), {
            petId: petId,
            date: Timestamp.fromDate(new Date(date)),
            name: name,
            created: Timestamp.fromDate(new Date()),
            updated: Timestamp.fromDate(new Date()),
            creatorId: userId
        });
        return true;
    }   catch (error) {
        console.error(error);
        return false;
    }
}

export const deleteVaccineFromPet = async (vaccineId) => {
    try {
        const vaccine = doc(db, "vaccines", vaccineId);
        await deleteDoc(vaccine);
        return true;
    } catch (error) {
        console.error(error);
        return false;
    }
}


/**
 * agrega una consulta médica a una mascota
 * @param {string} petId id de la mascota
 * @param {string} date fecha de la consulta
 * @param {string} diagnosis diagnóstico
 * @param {array} treatment tratamiento
 * @param {string} userId id del usuario que agrega la consulta
 * @returns 
 */
export const addMedicalHistoryToPet = async (petId, date, diagnosis, treatment, userId) => {
    try {
        //console.log("addMedicalHistoryToPet", petId, date, diagnosis, treatment, userId);
        const medicalHistory = collection(db, "medicalHistory");
        const newMedicalHistory = await setDoc(doc(medicalHistory), {
            petId: petId,
            date: Timestamp.fromDate(new Date(date)),
            diagnosis: diagnosis,
            treatment: treatment,
            created: Timestamp.fromDate(new Date()),
            updated: Timestamp.fromDate(new Date()),
            creatorId: userId
        });
        return true;
    }   catch (error) {
        console.error(error);
        return false;
    }
}


/**
 * Obtiene el historial médico de una mascota
 * @param {string} petId id de la mascota
 * @returns {array} retorna un arreglo con el historial médico de la mascota
 */
export const getMedicalHistoryByPet = async (petId) => {
    try {
        const medicalHistoryRef = collection(db, "medicalHistory");
        const q = query(medicalHistoryRef, where("petId", "==", petId));
        const querySnapshot = await getDocs(q);

        let medicalHistoryArray = [];
        querySnapshot.forEach((doc) => {
            let medicalHistory = doc.data();
            medicalHistory.id = doc.id;
            medicalHistoryArray.push(medicalHistory);
        });

        return medicalHistoryArray;
    } catch (error) {
        console.error("Error getting medical history by pet:", error);
        return [];
    }
}


/**
 * Obtiene las vacunas de una mascota
 * @param {string} petId id de la mascota
 * @returns {array} retorna un arreglo con las vacunas de la mascota
 */
export const getVaccinesByPet = async (petId) => {
    try {
        const vaccinesRef = collection(db, "vaccines");
        const q = query(vaccinesRef, where("petId", "==", petId));
        const querySnapshot = await getDocs(q);

        let vaccinesArray = [];
        querySnapshot.forEach((doc) => {
            let vaccine = doc.data();
            vaccine.id = doc.id;
            vaccinesArray.push(vaccine);
        });

        return vaccinesArray;
    } catch (error) {
        console.error("Error getting vaccines by pet:", error);
        return [];
    }
}

/**
 * Función para subir una foto de una mascota a Firebase Storage
 * @param {string} petId id de la mascota
 * @param {string} photo url de la foto
 * @returns 
 */
export const updatePetPhoto = async (petId, photo) => {
    try {
        const petRef = doc(db, "pets", petId);
        await setDoc(petRef, {photo: photo}, {merge: true});
        return true;
    } catch (error) {
        console.error(error);
        return false;
    }
}